import React from 'react'

/**
 * Renders a string containing inline markdown (links and bold) as React nodes.
 * Supports:
 *   [text](url)        → <a href={url} target="_blank" rel="noopener noreferrer" className="underline">
 *   **text**           → <strong>
 *   [**text**](url)    → bold link (bold inside the link label)
 *   **[text](url)**    → bold link (link inside the bold)
 *
 * Used in youth-sports section components (TlDrSection, BottomLineSection,
 * CostBreakdownSection, SeasonCalendarSection, ProgramProfilesExpander, etc.)
 * so that content authors can use standard markdown syntax in frontmatter strings.
 */

// Process **bold** spans inside an already-extracted string (e.g. a link label).
function renderBold(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  const re = /\*\*([^*]+)\*\*/g
  let last = 0
  let m: RegExpExecArray | null
  let k = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    out.push(
      <strong key={`${keyPrefix}-s${k++}`} className="font-semibold">
        {m[1]}
      </strong>,
    )
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

function link(
  href: string,
  children: React.ReactNode,
  key: number
): React.ReactNode {
  return (
    <a
      key={key}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline"
    >
      {children}
    </a>
  )
}

export function renderInlineMarkdown(text: string): React.ReactNode[] {
  const pattern = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g
  const result: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let keyIdx = 0

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index))
    }
    if (match[1] !== undefined) {
      // [label](url) — the label may itself contain **bold**
      result.push(link(match[2], renderBold(match[1], `l${keyIdx}`), keyIdx++))
    } else {
      // **content** — the content may itself be a [link](url)
      const inner = match[3]
      const innerLink = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(inner)
      if (innerLink) {
        result.push(
          <strong key={keyIdx++} className="font-semibold">
            {link(innerLink[2], innerLink[1], keyIdx)}
          </strong>
        )
      } else {
        result.push(
          <strong key={keyIdx++} className="font-semibold">
            {inner}
          </strong>
        )
      }
    }
    lastIndex = match.index + match[0].length
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex))
  }

  return result
}
